import {
    defineComponent,
    ref
} from "vue";
import {
    LogscordLandingProductHighlights,
    LogscordLandingTestimonials,
    LogscordLandingUseCases,
} from "@logscord/public-site-config";
import {
    LandingTimelinePreview,
    PublicFooter,
    PublicNavbar,
} from "./PublicShell.js";
import PwaInstallButton from "./PwaInstallButton.js";
import { usePublicReveal } from "./usePublicReveal.js";
import { useLocale } from "../../locales/i18n.js";


//////////////////////////////////////////////////
//  LOGSCORD LANDING
//////////////////////////////////////////////////


export default defineComponent({

    setup() {
        const { i18n } = useLocale();
        const pageElement = ref<HTMLElement | null>(null);

        usePublicReveal(pageElement);

        const heroTitle = () => i18n("landing.hero.title").split("\n");

        return () => (
            <div class="landing-page" ref={pageElement}>
                <PublicNavbar />

                <div class="landing-hero" id="product">
                    <div class="landing-hero-copy" data-reveal="hero-copy">
                        <span class="landing-kicker">{i18n("landing.hero.kicker")}</span>
                        <h1>
                            {heroTitle().map((line, index) => (
                                <>
                                    {index > 0 && <br />}
                                    {line}
                                </>
                            ))}
                        </h1>

                        <p>{i18n("landing.hero.description")}</p>

                        <div class="landing-hero-actions">
                            <a class="landing-button landing-button-dark" href="/api/oauth2">
                                {i18n("public.cta.use")}
                                <i class="material-symbols-outlined">arrow_forward</i>
                            </a>

                            <PwaInstallButton buttonClass="landing-button landing-button-install landing-button-install-hero" />

                            <a class="landing-demo-link" href="#timeline">
                                <i class="material-symbols-outlined">play_arrow</i>
                                {i18n("public.cta.demo")}
                            </a>
                        </div>
                    </div>

                    <div data-reveal="hero-shot" style="--reveal-delay: 120ms">
                        <LandingTimelinePreview />
                    </div>
                </div>

                <div class="landing-highlights" id="features">
                    {LogscordLandingProductHighlights.map((highlight, index) => (
                        <div data-reveal="highlight" style={`--reveal-delay: ${index * 70}ms`}>
                            <i class="material-symbols-outlined">{highlight.icon}</i>
                            <h2>{i18n(highlight.titleKey)}</h2>
                            <p>{i18n(highlight.descriptionKey)}</p>
                        </div>
                    ))}
                </div>

                <div class="landing-time-machine" id="timeline">
                    <div data-reveal="section-copy">
                        <span class="landing-kicker">{i18n("landing.timeline.kicker")}</span>
                        <h2>{i18n("landing.timeline.title")}</h2>
                        <p>{i18n("landing.timeline.description")}</p>
                    </div>

                    <div class="landing-time-visual" data-reveal="timeline-visual" style="--reveal-delay: 120ms">
                        <div class="landing-time-bubble">{i18n("landing.timeline.bubble")}<br /><strong>11:11</strong></div>
                        <div class="landing-time-line">
                            <span />
                            <span />
                            <span class="active" />
                            <span />
                            <span />
                        </div>

                        <div class="landing-state-cards">
                            <div>
                                <small>{i18n("landing.timeline.before")}</small>
                                <strong>{i18n("landing.timeline.before.users")}</strong>
                                <strong>{i18n("landing.timeline.before.channels")}</strong>
                                <strong>{i18n("landing.timeline.before.roles")}</strong>
                            </div>

                            <i class="material-symbols-outlined">arrow_forward</i>

                            <div>
                                <small>{i18n("landing.timeline.after")}</small>
                                <strong>{i18n("landing.timeline.after.users")}</strong>
                                <strong>{i18n("landing.timeline.after.channels")}</strong>
                                <strong>{i18n("landing.timeline.after.sanctions")}</strong>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="landing-use-cases" id="cases">
                    <div class="landing-section-heading" data-reveal="section-heading">
                        <span class="landing-kicker">{i18n("landing.use.kicker")}</span>
                        <h2>{i18n("landing.use.title")}</h2>
                    </div>

                    <div class="landing-use-grid">
                        {LogscordLandingUseCases.map((useCase, index) => (
                            <div data-reveal="use-case" style={`--reveal-delay: ${index * 80}ms`}>
                                <span>
                                    <i class="material-symbols-outlined">{useCase.icon}</i>
                                    {i18n(useCase.labelKey)}
                                </span>
                                <h3>{i18n(useCase.titleKey)}</h3>
                                <p>{i18n(useCase.descriptionKey)}</p>
                            </div>
                        ))}
                    </div>
                </div>

                <div class="landing-testimonials" id="testimonials">
                    <div class="landing-section-heading" data-reveal="section-heading">
                        <span class="landing-kicker">{i18n("landing.testimonials.kicker")}</span>
                        <h2>{i18n("landing.testimonials.title")}</h2>
                    </div>

                    <div class="landing-testimonial-grid">
                        {LogscordLandingTestimonials.map((testimonial, index) => (
                            <div data-reveal="testimonial" style={`--reveal-delay: ${index * 80}ms`}>
                                <p>“{i18n(testimonial.quoteKey)}”</p>
                                <div class="landing-testimonial-author">
                                    <div class="landing-testimonial-avatar">{testimonial.avatar}</div>

                                    <div>
                                        <strong>{i18n(testimonial.nameKey)}</strong>
                                        <span>{i18n(testimonial.titleKey)}</span>
                                        <small>{i18n(testimonial.metricKey)}</small>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div class="landing-final-cta" data-reveal="final-cta">
                    <div>
                        <span class="landing-kicker">{i18n("landing.final.kicker")}</span>
                        <h2>{i18n("landing.final.title")}</h2>
                        <p>{i18n("landing.final.description")}</p>
                    </div>

                    <a class="landing-button landing-button-dark" href="/api/oauth2">
                        {i18n("public.cta.start_now")}
                        <i class="material-symbols-outlined">arrow_forward</i>
                    </a>
                </div>

                <PublicFooter />
            </div>
        );
    },
});
